// Constants
import { MOCK_JOB_TITLE } from '@shared/mocks';
import { PATH_TO_TITLE_PAGE } from '@/constants';
import { DATE_FORMATS } from '@shared/constants';

// Types
import { DeepPartial, Resume, FormSkill, FormJourney } from '@shared/types';

// Utils
import {
  convertISODateToDateFormat,
  hasProperty,
  parseCertificationDataToStore,
  parseEducationDataToStore,
  parseJourneyDataToStore,
  parseTechnicalSkillsDataToStore,
  formatDayMonthYearLabel,
  toSnakeCase,
} from '@shared/utils';

// Types
import { TemplateProps } from '@/components/template/resume/type';
import { JourneyFormValue } from '@/schemas';

interface JobTitle {
  label: string;
  value: string;
}

/**
 * Finds the label for a given job title value in MOCK_JOB_TITLE
 * @param value - The job title value to look up
 * @returns string - The corresponding label or the original value if not found
 */
const getJobTitleLabel = (value: string): string =>
  MOCK_JOB_TITLE.find((job: JobTitle) => job.value === value)?.label ?? value;

/**
 * Entries and find the path for a value in PATH_TO_TITLE_PAGE
 * @param string - The pathname
 * @returns string - The title with value map text in PATH_TO_TITLE_PAGE
 */
export const getPageTitleFromPath = (pathname: string) => {
  const matchedEntry = Object.entries(PATH_TO_TITLE_PAGE).find(([route]) =>
    pathname.includes(route),
  );

  return matchedEntry?.[1];
};

/**
 * Converts a Resume type to ResumeData type with optional preview image
 * @param resume - Resume object to convert
 * @param previewImageBase64 - Optional base64 string of preview image
 * @returns ResumeData object
 */
const convertResumeToTemplateProps = (
  resume: Resume,
  previewImageBase64?: string,
): TemplateProps['data'] => {
  if (!hasProperty(resume.info, 'name')) {
    return {
      info: {
        name: '',
        email: '',
        phone: '',
        bio: '',
        title: '',
        address: '',
        avatar: '',
        previewImageBase64: '',
        links: [],
      },
      journeys: [],
      technicalSkills: [],
      educations: [],
      certs: [],
    };
  }

  const info = {
    ...resume.info,
    email: resume.id || '',
    name: resume.info?.name || '',
    phone: resume.info?.phone || '',
    bio: resume.info?.bio || '',
    title: getJobTitleLabel(resume.info?.title || ''),
    address: resume.info?.address || '',
    avatar: resume.info?.avatar || '',
    previewImageBase64: previewImageBase64 || resume.info?.avatar || '',
    birthday: resume.info?.birthday
      ? convertISODateToDateFormat(resume.info?.birthday)
      : '',
    links:
      resume.links?.map((link) => ({
        id: link.id || '',
        name: link.name || '',
        url: link.url || '',
      })) || [],
  };

  return {
    info,
    journeys: parseJourneyDataToStore(resume.journeys || []),
    technicalSkills: parseTechnicalSkillsDataToStore(resume.skills || []),
    educations: parseEducationDataToStore(resume.education || []),
    certs: parseCertificationDataToStore(resume.certs || []),
  };
};

/**
 * Checks if an journey item has at least one valid value
 * @param journey The journey item to check
 * @returns true if the journey has at least one valid value, false otherwise
 */
const isValidJourney = (journey: DeepPartial<JourneyFormValue>): boolean => {
  if (!journey) return false;

  // Check if company, title, or address have valid values
  if (
    journey.company?.trim() ||
    journey.title?.trim() ||
    journey.address?.trim() ||
    journey.start?.trim() ||
    journey.end?.trim()
  ) {
    return true;
  }

  // Check if any project has valid values
  if (journey.projects && journey.projects?.length > 0) {
    for (const project of journey.projects) {
      if (
        project?.name?.trim() ||
        project?.desc?.trim() ||
        project?.duties?.trim() ||
        project?.role?.trim() ||
        project?.start?.trim() ||
        project?.end?.trim() ||
        (project?.stacks &&
          project.stacks?.length > 0 &&
          project.stacks.some(
            (stack) =>
              stack?.name?.trim() ||
              (stack?.items &&
                stack.items?.length > 0 &&
                stack.items.some((item) => item?.trim())),
          ))
      ) {
        return true;
      }
    }
  }

  // If we reach here, no valid values were found
  return false;
};

export { getJobTitleLabel, convertResumeToTemplateProps, isValidJourney };

/**
 * Filters skill categories to keep only those with a name or at least one non-empty item
 * @param skills - List of technical skill categories
 * @returns Filtered list containing only categories with meaningful content
 */
export const filterValidSkills = (skills: FormSkill[]): FormSkill[] =>
  skills.filter(
    ({ name: skillName = '', items = [] }) =>
      skillName.trim() || items.some((item: string) => item?.trim()),
  );

/**
 * Formats a date range string from start and end date values
 * @param start - Start date string
 * @param end - End date string
 * @returns Formatted range string e.g. "Jan 2020 - Mar 2022", or a single date if only one is provided
 */
export const buildDateRange = (start: string, end: string): string => {
  if (!start) return '';

  const startFormatted = formatDayMonthYearLabel(start, 'MMM YYYY');
  const endFormatted = end
    ? formatDayMonthYearLabel(end, 'MMM YYYY')
    : 'Present';

  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Joins all technology stack items from a project into a comma-separated string
 * @param stacks - Technology stacks each containing an items list
 * @returns Comma-separated technology items, or an empty string if none exist
 */
export const buildTechStackText = (
  stacks: Array<{ items?: string[] | null }>,
): string =>
  stacks
    .flatMap((stack) => stack.items ?? [])
    .filter(Boolean)
    .join(', ');

/**
 * Collects all tech-stack items across multiple projects, deduplicates them,
 * and returns a single comma-separated string
 * @param projects - Array of projects each containing a stacks list
 * @returns Deduplicated comma-separated technology items
 */
export const buildMergedTechStackText = (
  projects: Array<{ stacks?: Array<{ items?: string[] }> }>,
): string => {
  const allItems = projects
    .flatMap((p) => p.stacks ?? [])
    .flatMap((stack) => stack.items ?? [])
    .filter(Boolean);

  return [...new Set(allItems)].join(', ');
};

export type FlatProject = {
  project: NonNullable<FormJourney['projects']>[number];
  role: string;
};

/**
 * Flattens all projects across journeys, pairing each with its own role.
 * @param journeys - Array of journey entries
 * @returns Flat array of project-role pairs
 */
export const flattenJourneyProjects = (
  journeys: FormJourney[],
): FlatProject[] =>
  journeys.flatMap((experience) => {
    const { projects = [] } = experience ?? {};

    return (projects ?? []).map((project) => ({
      project,
      role: project?.role ?? '',
    }));
  });

/**
 * Combines a school name with its year in parentheses when a date is available
 * @param school - School or institution name
 * @param graduation - Graduation date string
 * @returns Formatted label like "MIT (2018)", or just the school name when no year is found
 */
export const buildSchoolHeader = (
  school: string,
  graduation: string,
): string => {
  const yearLabel = graduation
    ? formatDayMonthYearLabel(graduation, 'YYYY')
    : '';

  return yearLabel ? `${school} (${yearLabel})` : school;
};

/**
 * Splits a semicolon-delimited major string into bullet items, returning an empty array when a degree grade is present since that case renders as plain text
 * @param major - Semicolon-separated subject strings
 * @param degree - Degree grade that suppresses bullet rendering when non-empty
 * @returns Trimmed non-empty subject strings e.g. ["Computer Science", "AI"]
 */
export const parseMajorBullets = (major: string, degree: string): string[] =>
  major && !degree
    ? major
        .split(/;\s*/)
        .map((part) => part.trim())
        .filter(Boolean)
    : [];

/**
 * Derives the degree label and plain-text display line used when a grade is present
 * @param major - Field of study
 * @param degree - Degree grade value
 * @returns Object with degreeLabel and plainTextLine e.g. { degreeLabel: "Grade: A", plainTextLine: "CS | Grade: A" }
 */
export const buildEducationGradeInfo = (
  major: string,
  degree: string,
): { degreeLabel: string; plainTextLine: string } => {
  const degreeLabel = degree ? `Grade: ${degree}` : '';
  const plainTextLine =
    degree && major ? [major, degreeLabel].filter(Boolean).join(' | ') : '';

  return { degreeLabel, plainTextLine };
};

/**
 * Builds a pipe-separated education line for resume templates.
 * Degree and major are comma-joined when both are present.
 * Empty segments are omitted so partial data renders cleanly.
 * @param school - Institution name
 * @param degree - Field of study or degree label
 * @param major - Major or specialization (optional)
 * @param graduation - ISO or formatted graduation date string
 * @returns Formatted string e.g. "School | Degree of Major | Mon YYYY"
 */
export const buildEducationLine = (
  school: string,
  degree: string,
  major: string,
  graduation: string,
): string => {
  const yearLabel = graduation
    ? formatDayMonthYearLabel(graduation, DATE_FORMATS.SHORT_MONTH_YEAR)
    : '';
  const degreeMajor = [degree, major].filter(Boolean).join(' of ');
  return [school, degreeMajor, yearLabel].filter(Boolean).join(' | ');
};

/** Builds a timestamped snake_case filename for resume exports */
export const buildFileName = (ext: string, name?: string) =>
  name
    ? `${toSnakeCase(`${name} ${Date.now()}`)}.${ext}`
    : `resume_${Date.now()}.${ext}`;
